import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { pageSchema } from '@/api/types';
import { tokenStore } from '@/api/tokenStore';

/**
 * Support sessions.
 *
 * Entering a merchant's account swaps the access token the whole app sends.
 * The staff member's own token is held here, in memory, so leaving is just
 * putting it back - there is no second login and no durable copy of either
 * credential anywhere a script could read later.
 *
 * A page reload loses this state on purpose. The refresh cookie belongs to the
 * staff account, so a reload lands back in the platform panel as themselves,
 * which is the safe direction to fail.
 */

export const impersonationSessionSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string().nullable(),
  actor_user_id: z.string(),
  actor_name: z.string().nullable(),
  target_user_id: z.string(),
  target_email: z.string().nullable(),
  kind: z.enum(['SUPPORT_VIEW', 'SETUP_ASSIST']).optional(),
  reason: z.string(),
  expires_at: z.string(),
  ended_at: z.string().nullable(),
  created_at: z.string(),
});

export const impersonationGrantSchema = z.object({
  session: impersonationSessionSchema,
  access_token: z.string(),
  expires_in: z.number(),
  read_only: z.boolean(),
});

export type ImpersonationSession = z.infer<typeof impersonationSessionSchema>;
export type ImpersonationGrant = z.infer<typeof impersonationGrantSchema>;

export function startImpersonation(
  tenantId: string,
  payload: { reason: string; target_user_id?: string },
) {
  return apiRequest(`/platform/merchants/${tenantId}/impersonate`, impersonationGrantSchema, {
    method: 'POST',
    body: payload,
  });
}

export function endImpersonation(sessionId: string) {
  return apiRequest(`/platform/impersonation/${sessionId}`, impersonationSessionSchema, {
    method: 'DELETE',
  });
}

function tenantQuery(tenantId?: string) {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
}

/**
 * Every open session across all staff, or inside one merchant's account.
 * Platform owner only.
 */
export function fetchLiveImpersonations(tenantId?: string) {
  return apiRequest(
    `/platform/impersonation/live${tenantQuery(tenantId)}`,
    z.array(impersonationSessionSchema),
  );
}

/** Close every open session at once, or every one inside one merchant. Platform owner only. */
export function endAllImpersonations(tenantId?: string) {
  return apiRequest(
    `/platform/impersonation/end-all${tenantQuery(tenantId)}`,
    z.object({ ended: z.number() }),
    { method: 'POST' },
  );
}

export function fetchImpersonationHistory(params: { page?: number; tenant_id?: string } = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  const query = search.toString();
  return apiRequest(
    `/platform/impersonation${query ? `?${query}` : ''}`,
    pageSchema(impersonationSessionSchema),
  );
}

// --- The live session, held in memory --------------------------------------

interface ActiveImpersonation {
  session: ImpersonationSession;
  /** The staff member's own token, restored on exit. */
  staffToken: string | null;
  readOnly: boolean;
}

type Listener = (active: ActiveImpersonation | null) => void;

let active: ActiveImpersonation | null = null;
const listeners = new Set<Listener>();

function publish() {
  listeners.forEach((listener) => listener(active));
}

export const impersonationStore = {
  get(): ActiveImpersonation | null {
    return active;
  },

  isActive(): boolean {
    return active !== null;
  },

  /** Swap into the merchant's account, keeping the staff token for the exit. */
  begin(grant: ImpersonationGrant): void {
    active = {
      session: grant.session,
      staffToken: tokenStore.get(),
      readOnly: grant.read_only,
    };
    tokenStore.set(grant.access_token);
    // A 401 now means the session ended, not that the token aged out. Letting
    // the client refresh would swap the staff identity back in behind the
    // scenes and retry the merchant's request as them.
    tokenStore.setRefreshable(false);
    publish();
  },

  /**
   * Return to the platform panel.
   *
   * Restores the staff token even when the server call failed - a session that
   * could not be ended cleanly must still not leave the browser stuck holding a
   * merchant's credentials.
   */
  end(): void {
    const staffToken = active?.staffToken ?? null;
    active = null;
    tokenStore.set(staffToken);
    tokenStore.setRefreshable(true);
    publish();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
